import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon, TrashIcon, CheckIcon } from "@heroicons/react/24/outline";
import {
  getMoveOutReminders,
  createMoveOutReminder,
  deleteMoveOutReminder,
  IMoveOutReminder,
  ICreateMoveOutReminder,
} from "./DataServices/MoveOutReminderDataServices";

interface MoveOutReminderModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  listingId: string;
  listingTitle: string;
}

export default function MoveOutReminderModal({
  open,
  setOpen,
  listingId,
  listingTitle,
}: MoveOutReminderModalProps) {
  const [reminders, setReminders] = useState<IMoveOutReminder[]>([]);
  const [days, setDays] = useState<string>("");
  const [hours, setHours] = useState<string>("");
  const [minutes, setMinutes] = useState<string>("");
  const [showingDays, setShowingDays] = useState<string>("");
  const [showingHours, setShowingHours] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const hasSeededShowingReminderRef = useRef(false);
  const [notification, setNotification] = useState<{
    show: boolean;
    message: string;
    type: "success" | "error";
  }>({ show: false, message: "", type: "success" });

  const loadReminders = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getMoveOutReminders(listingId);

      const showingReminders = data.filter(
        (r) => r.reminderType === "ShowingApartmentIncomplete",
      );

      if (
        showingReminders.length === 0 &&
        !hasSeededShowingReminderRef.current
      ) {
        const created = await createMoveOutReminder({
          listingId,
          reminder: 2 * 24 * 60,
          isReminded: false,
          reminderType: "ShowingApartmentIncomplete",
        });

        if (created) {
          hasSeededShowingReminderRef.current = true;
          const refreshed = await getMoveOutReminders(listingId);
          setReminders(refreshed);
          showNotification(
            "Default showing reminder set to 2 days.",
            "success",
          );
          return;
        }
      }

      setReminders(data);
    } catch (error) {
      console.error("Error loading reminders:", error);
      showNotification("Failed to load reminders", "error");
    } finally {
      setLoading(false);
    }
  }, [listingId]);

  useEffect(() => {
    if (open && listingId) {
      hasSeededShowingReminderRef.current = false;
      loadReminders();
    }
  }, [open, listingId, loadReminders]);

  const showNotification = (message: string, type: "success" | "error") => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: "", type: "success" });
    }, 3000);
  };

  const handleAddReminder = async () => {
    // Parse and validate inputs
    const daysNum = parseInt(days || "0");
    const hoursNum = parseInt(hours || "0");
    const minutesNum = parseInt(minutes || "0");

    if (isNaN(daysNum) || isNaN(hoursNum) || isNaN(minutesNum)) {
      showNotification("Please enter valid numbers", "error");
      return;
    }

    if (
      daysNum < 0 ||
      hoursNum < 0 ||
      hoursNum > 23 ||
      minutesNum < 0 ||
      minutesNum > 59
    ) {
      showNotification("Invalid input. Hours: 0-23, Minutes: 0-59", "error");
      return;
    }

    // Convert to total minutes
    const totalMinutes = daysNum * 24 * 60 + hoursNum * 60 + minutesNum;

    if (totalMinutes <= 0) {
      showNotification("Please enter a valid time greater than 0", "error");
      return;
    }

    // Check if reminder already exists
    if (
      reminders.some(
        (r) => r.reminder === totalMinutes && r.reminderType === "MoveOut",
      )
    ) {
      showNotification("Reminder already exists for this time", "error");
      return;
    }

    const newReminder: ICreateMoveOutReminder = {
      listingId: listingId,
      reminder: totalMinutes,
      isReminded: false,
      reminderType: "MoveOut",
    };

    setLoading(true);
    try {
      const success = await createMoveOutReminder(newReminder);
      if (success) {
        showNotification("Reminder added successfully!", "success");
        setDays("");
        setHours("");
        setMinutes("");
        await loadReminders();
      } else {
        showNotification("Failed to add reminder", "error");
      }
    } catch (error) {
      console.error("Error adding reminder:", error);
      showNotification("Failed to add reminder", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleAddShowingReminderDelay = async () => {
    // Parse and validate inputs
    const daysNum = parseInt(showingDays || "0");
    const hoursNum = parseInt(showingHours || "0");

    if (isNaN(daysNum) || isNaN(hoursNum)) {
      showNotification(
        "Please enter valid numbers for showing reminder delay",
        "error",
      );
      return;
    }

    if (daysNum < 0 || hoursNum < 0 || hoursNum > 23) {
      showNotification(
        "Invalid input. Days must be 0 or more, Hours: 0-23",
        "error",
      );
      return;
    }

    // Convert to total minutes
    const totalMinutes = daysNum * 24 * 60 + hoursNum * 60;

    if (totalMinutes < 0) {
      showNotification("Please enter a valid time", "error");
      return;
    }

    // Check if reminder already exists
    const existingShowingReminder = reminders.find(
      (r) => r.reminderType === "ShowingApartmentIncomplete",
    );

    if (existingShowingReminder) {
      showNotification(
        "A showing apartment incomplete delay is already configured. Delete it first to add a new one.",
        "error",
      );
      return;
    }

    const newReminder: ICreateMoveOutReminder = {
      listingId: listingId,
      reminder: totalMinutes,
      isReminded: false,
      reminderType: "ShowingApartmentIncomplete",
    };

    setLoading(true);
    try {
      const success = await createMoveOutReminder(newReminder);
      if (success) {
        showNotification(
          "Showing apartment incomplete delay set successfully!",
          "success",
        );
        setShowingDays("");
        setShowingHours("");
        await loadReminders();
      } else {
        showNotification("Failed to set delay", "error");
      }
    } catch (error) {
      console.error("Error setting showing reminder delay:", error);
      showNotification("Failed to set delay", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReminder = async (id: string) => {
    setLoading(true);
    try {
      const success = await deleteMoveOutReminder(id);
      if (success) {
        showNotification("Reminder deleted successfully!", "success");
        hasSeededShowingReminderRef.current = true;
        await loadReminders();
      } else {
        showNotification("Failed to delete reminder", "error");
      }
    } catch (error) {
      console.error("Error deleting reminder:", error);
      showNotification("Failed to delete reminder", "error");
    } finally {
      setLoading(false);
    }
  };

  const moveOutReminders = reminders.filter(
    (r) => r.reminderType === "MoveOut",
  );
  const showingReminders = reminders.filter(
    (r) => r.reminderType === "ShowingApartmentIncomplete",
  );
  const effectiveShowingReminder = showingReminders.reduce<
    IMoveOutReminder | undefined
  >((largest, current) => {
    if (!largest) {
      return current;
    }

    return current.reminder > largest.reminder ? current : largest;
  }, undefined);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={setOpen}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl">
                <div className="bg-gray-900 px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-semibold text-white"
                      >
                        Reminder Configuration - {listingTitle}
                      </Dialog.Title>
                      <p className="mt-1 text-sm text-gray-300">
                        Manage move-out and showing reminder entries.
                      </p>
                    </div>
                    <button
                      type="button"
                      className="ml-3 rounded-md text-gray-300 hover:text-white focus:outline-none"
                      onClick={() => setOpen(false)}
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>
                </div>

                <div className="px-6 py-6 bg-white">
                  {notification.show && (
                    <div
                      className={`mb-4 rounded-md p-3 ${
                        notification.type === "success"
                          ? "bg-green-50 text-green-700 border border-green-200"
                          : "bg-red-50 text-red-700 border border-red-200"
                      }`}
                    >
                      <p className="text-sm">{notification.message}</p>
                    </div>
                  )}

                  <h4 className="text-sm font-medium text-gray-900 mb-4">
                    Move Out Email
                  </h4>
                  <div className="mb-6 bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Add New Reminder
                    </label>
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">
                              Days
                            </label>
                            <input
                              type="number"
                              min="0"
                              value={days}
                              onChange={(e) => setDays(e.target.value)}
                              placeholder="0"
                              className="block w-full rounded-md border-gray-300 px-3 py-2 text-sm placeholder-gray-400 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">
                              Hours
                            </label>
                            <input
                              type="number"
                              min="0"
                              max="23"
                              value={hours}
                              onChange={(e) => setHours(e.target.value)}
                              placeholder="0"
                              className="block w-full rounded-md border-gray-300 px-3 py-2 text-sm placeholder-gray-400 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">
                              Minutes
                            </label>
                            <input
                              type="number"
                              min="0"
                              max="59"
                              value={minutes}
                              onChange={(e) => setMinutes(e.target.value)}
                              placeholder="0"
                              className="block w-full rounded-md border-gray-300 px-3 py-2 text-sm placeholder-gray-400 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            />
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleAddReminder}
                        disabled={loading}
                        className="inline-flex items-center rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Add
                      </button>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-3">
                      Configured Reminders
                    </h4>
                    {loading && reminders.length === 0 ? (
                      <div className="flex justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
                      </div>
                    ) : moveOutReminders.length === 0 ? (
                      <div className="text-center py-8 px-4 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                        <p className="text-sm text-gray-500">
                          No reminders configured. Add one above to get started.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-1.5 max-h-48 overflow-y-auto">
                        {moveOutReminders.map((reminder) => {
                          const totalMinutes = reminder.reminder;
                          const days = Math.floor(totalMinutes / (24 * 60));
                          const remainingMinutes = totalMinutes % (24 * 60);
                          const hours = Math.floor(remainingMinutes / 60);
                          const minutes = remainingMinutes % 60;

                          let timeDisplay = "";
                          if (days > 0) timeDisplay += `${days}d `;
                          if (hours > 0) timeDisplay += `${hours}h `;
                          if (minutes > 0) timeDisplay += `${minutes}m`;
                          if (timeDisplay === "") timeDisplay = "0m";

                          return (
                            <div
                              key={reminder.id}
                              className="flex items-center justify-between p-2.5 bg-gray-50 border border-gray-200 rounded hover:bg-gray-100 transition-colors"
                            >
                              <div className="flex items-center gap-2">
                                <div className="flex-shrink-0">
                                  <div className="w-auto min-w-[4rem] px-2 h-8 bg-white rounded border border-gray-300 flex items-center justify-center">
                                    <span className="text-sm font-semibold text-gray-900">
                                      {timeDisplay.trim()}
                                    </span>
                                  </div>
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-gray-900">
                                    {timeDisplay.trim()} before move-out
                                  </p>
                                  {reminder.isReminded && (
                                    <span className="inline-flex items-center mt-0.5 px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                                      <CheckIcon className="h-2.5 w-2.5 mr-0.5" />
                                      Sent
                                    </span>
                                  )}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() =>
                                  handleDeleteReminder(reminder.id)
                                }
                                disabled={loading}
                                className="flex-shrink-0 p-1.5 text-gray-400 hover:text-red-600 hover:bg-white rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-transparent hover:border-red-200"
                                title="Delete reminder"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <h4 className="text-sm font-medium text-gray-900 mb-4">
                      Showing Apartment Incomplete Reminder
                    </h4>
                    <p className="text-xs text-gray-600 mb-4">
                      Set how many hours/days BEFORE the showing date to notify
                      manager if tenant hasn't confirmed.
                    </p>

                    <div className="mb-6 bg-blue-50 rounded-lg p-4 border border-blue-200">
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Manager Follow-up Deadline (Before Showing Date)
                      </label>
                      <div className="flex gap-3">
                        <div className="flex-1">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">
                                Days
                              </label>
                              <input
                                type="number"
                                min="0"
                                value={showingDays}
                                onChange={(e) => setShowingDays(e.target.value)}
                                placeholder="0"
                                className="block w-full rounded-md border-gray-300 px-3 py-2 text-sm placeholder-gray-400 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">
                                Hours
                              </label>
                              <input
                                type="number"
                                min="0"
                                max="23"
                                value={showingHours}
                                onChange={(e) =>
                                  setShowingHours(e.target.value)
                                }
                                placeholder="0"
                                className="block w-full rounded-md border-gray-300 px-3 py-2 text-sm placeholder-gray-400 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                              />
                            </div>
                          </div>
                          <p className="mt-1.5 text-xs text-gray-500">
                            e.g., 2 days 4 hours = notify manager if tenant
                            doesn't confirm before showing date
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={handleAddShowingReminderDelay}
                          disabled={loading}
                          className="inline-flex items-center rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Set
                        </button>
                      </div>
                    </div>

                    <div>
                      <h5 className="text-xs font-medium text-gray-700 mb-2">
                        Current Configuration
                      </h5>
                      {effectiveShowingReminder ? (
                        <div className="space-y-2">
                          {showingReminders.length > 1 && (
                            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                              Multiple showing reminder rows exist. The largest
                              value is used as the effective configuration.
                            </p>
                          )}
                          <div className="flex items-center justify-between p-2.5 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 transition-colors">
                            <div className="flex items-center gap-2">
                              <div className="flex-shrink-0">
                                <div className="w-auto min-w-[6rem] px-2 h-8 bg-white rounded border border-blue-300 flex items-center justify-center">
                                  <span className="text-sm font-semibold text-blue-900">
                                    {(() => {
                                      const totalMinutes =
                                        effectiveShowingReminder.reminder;
                                      const reminderDays = Math.floor(
                                        totalMinutes / (24 * 60),
                                      );
                                      const remainingMinutes =
                                        totalMinutes % (24 * 60);
                                      const reminderHours = Math.floor(
                                        remainingMinutes / 60,
                                      );
                                      const reminderMinutes =
                                        remainingMinutes % 60;

                                      let timeDisplay = "";
                                      if (reminderDays > 0)
                                        timeDisplay += `${reminderDays}d `;
                                      if (reminderHours > 0)
                                        timeDisplay += `${reminderHours}h `;
                                      if (reminderMinutes > 0)
                                        timeDisplay += `${reminderMinutes}m`;
                                      if (timeDisplay === "")
                                        timeDisplay = "Immediate";

                                      return timeDisplay.trim();
                                    })()}
                                  </span>
                                </div>
                              </div>
                              <div>
                                <p className="text-xs font-medium text-gray-900">
                                  Notify manager before showing date
                                </p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                handleDeleteReminder(
                                  effectiveShowingReminder.id,
                                )
                              }
                              disabled={loading}
                              className="flex-shrink-0 p-1.5 text-gray-400 hover:text-red-600 hover:bg-white rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-transparent hover:border-red-200"
                              title="Delete configuration"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-4 px-4 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                          <p className="text-xs text-gray-500">
                            No reminder configured in the database.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
